import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Typography } from "antd"
import type React from "react"

const { Text } = Typography
import { useEffect, useState } from "react"
import SetSearchForm from "./components/SetSearchForm"
import SetTable from "./components/SetTable"
import SetTableActions from "./components/SetTableActions"
import UserSetVocabulariesModal from "./components/UserSetVocabulariesModal"
import { setService, type SetSearchParams, type SetApiParams } from "@/services/dictionary/setApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const UserSetPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<SetSearchParams>({
    ...PAGE_CONFIG,
    search: "",
    visibility: undefined,
    user_id: undefined,
    has_description: undefined,
  })
  const [total, setTotal] = useState<number>(0)
  const [selectedSetForVocabularies, setSelectedSetForVocabularies] = useState<any | null>(null)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["set_list", searchParams],
    queryFn: () => {
      const apiParams: SetApiParams = {
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search || undefined,
        visibility: searchParams.visibility || undefined,
        user_id: searchParams.user_id || undefined,
        has_description: searchParams.has_description || undefined,
        type: "user",
      }
      return setService.list(apiParams)
    },
  })

  useEffect(() => {
    if (result?.paginate.total) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const handleSearch = (values: Omit<SetSearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const tableLoading = isFetching

  return (
    <div className="flex flex-col gap-2">
      <style>{`
        .text-primary {
          color: var(--ant-color-primary, #1677ff) !important;
        }
        .border-primary {
          border-color: var(--ant-color-primary, #1677ff) !important;
        }
      `}</style>
      <SetSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          visibility: searchParams.visibility,
          user_id: searchParams.user_id,
          has_description: searchParams.has_description,
        }}
      />
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Bộ từ của người dùng</h2>
            <Divider orientation="vertical" />
            <Text type="secondary" className="text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</Text>
            <Divider orientation="vertical" />
            <SetTableActions onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <SetTable
          data={result?.data || []}
          loading={tableLoading}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          onViewVocabularies={(record) => setSelectedSetForVocabularies(record)}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>

      <UserSetVocabulariesModal
        open={!!selectedSetForVocabularies}
        record={selectedSetForVocabularies}
        onClose={() => setSelectedSetForVocabularies(null)}
      />
    </div>
  )
}

export default UserSetPage
